import { z } from "zod";
//#region src/scaling-intent.ts
/**
* Coarse sanity ceiling on the manifest-declared warm floor. This is anti-abuse only — the AUTHORITATIVE
* clamp is the DP admin limit (WS-2) applied at compile time (WS-3, DEC-D6). Kept generous; a REGIONAL pool
* multiplies this ×(#zones), so real deployments stay far below it (DEC-S1-b warns on the ×3-per-zone cost).
*/
const SCALING_INTENT_WARM_FLOOR_MAX = 50;
/** Min/max per-pool boot-disk override (GiB) for a DEDICATED pool's `nodeShape.diskGiB`. Baseline when
*  omitted is the composition's baked `diskSizeGb` (100; GPU 200) — see `as-built-node-pools.md`. */
const SCALING_INTENT_DISK_GIB_MIN = 10;
const SCALING_INTENT_DISK_GIB_MAX = 4096;
/**
* App-declared scaling intent for a fixture service or worker (Class-R). Compiled to node-pool topology by
* WS-3, clamped by the DP admin limits (WS-2). All fields optional; the whole block optional (D10).
*
* FIELDS
* - `dedicated` — request a DEDICATED per-app node pool instead of riding the shared machine-type ("demand")
*   pool. `false`/omitted ⇒ shared pool = today's behavior (D4). `true` ⇒ WS-3 emits a per-app pool
*   (naming/collision is Spike S3, WS-3). Compute-units are dedicated by a WS-3 DEFAULT regardless (D4) —
*   that default is NOT expressed here.
* - `warmFloor` — number of nodes to keep HOT (idle-but-ready) for fast scale-up. Compiles to the pool's
*   `minNodeCount` (WS-3), then admin-clamped (DEC-D6). `0`/omitted ⇒ no warm floor (scale from zero).
*   DEC-S1-b: warmth is opt-in precisely because a REGIONAL pool makes `minNodeCount ≥ 1` cost ×(#zones).
* - `scaleToZero` — whether the pool may drop to ZERO nodes when idle. Omitted/`true` ⇒ may scale to zero
*   (with `warmFloor` 0 → `minNodeCount 0`, today's demand-pool behavior). `false` ⇒ never below one node
*   even when idle (WS-3 floors `minNodeCount` at 1 when `warmFloor` is 0). Interaction: when `warmFloor ≥ 1`
*   the pool never reaches zero anyway, so `scaleToZero` only bites at `warmFloor` 0.
*   *** NAMING NOTE — this is NODE-pool scale-to-zero, DISTINCT from `FixtureTopology.scaleToZero`
*   (`FixtureScaleToZero`, pod-level KEDA-HTTP idle wake) and from `FixtureServiceScaling` (replica
*   autoscaling). Both axes may be set together; they control different layers (nodes vs pods). ***
* - `nodeShape` — per-pool node hardware overrides. Honored ONLY on a dedicated pool (a shared demand pool
*   cannot carry per-fixture disk/spot). The MACHINE TYPE is NOT here — it reuses the existing
*   `FixtureService.nodeType` / `FixtureWorker.nodeType`. `diskGiB` overrides the baked boot-disk size;
*   `spot` requests spot/preemptible nodes (cost/eviction trade-off). Both optional.
*
* CROSS-FIELD RULE (DD-1, fail-loud): if any of `warmFloor`/`scaleToZero`/`nodeShape` is present, `dedicated`
* MUST be `true` — those knobs are meaningless on a shared pool (DEC-S1-b). Mirrored in `fixture.schema.json`
* (`$defs/scalingIntent`, Ajv if/then) so the operator's Ajv accessor rejects the same shapes.
*/
const scalingIntentSchema = z.object({
	dedicated: z.boolean().optional(),
	warmFloor: z.number().int().min(0).max(50).optional(),
	scaleToZero: z.boolean().optional(),
	nodeShape: z.object({
		diskGiB: z.number().int().min(10).max(SCALING_INTENT_DISK_GIB_MAX).optional(),
		spot: z.boolean().optional()
	}).strict().optional()
}).strict().superRefine((v, ctx) => {
	if (v.dedicated === true) return;
	if (v.warmFloor !== void 0) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: ["warmFloor"],
		message: "warmFloor requires dedicated: true (a warm floor is only honored on a dedicated pool)"
	});
	if (v.scaleToZero !== void 0) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: ["scaleToZero"],
		message: "scaleToZero requires dedicated: true (node-pool scale-to-zero is only honored on a dedicated pool)"
	});
	if (v.nodeShape !== void 0) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: ["nodeShape"],
		message: "nodeShape requires dedicated: true (per-pool disk/spot is only honored on a dedicated pool)"
	});
});
//#endregion
export { SCALING_INTENT_DISK_GIB_MAX, SCALING_INTENT_DISK_GIB_MIN, SCALING_INTENT_WARM_FLOOR_MAX, scalingIntentSchema };

//# sourceMappingURL=scaling-intent.js.map